import React from "react";
import PropTypes from "prop-types";
import Plot from "react-plotly.js";

const THROTTLE_COLOR = '#00A86B'; // Green
const BRAKE_COLOR = '#FF1E1E';    // Red
const COAST_COLOR = '#888888';    // Grey

function getInputColor(p) {
  if (p.brake > 0) return BRAKE_COLOR;
  if (p.throttle > 0) return THROTTLE_COLOR;
  return COAST_COLOR;
}

export default function RaceLineHighlight({ lapId, lapData, trackLayout, distanceSliceIndices, axisRanges, onRelayout, currentIndices, selectedLaps, markerColor }) {
  if (!lapId || !lapData[lapId]) return null;
  const arr = lapData[lapId];
  const slice = distanceSliceIndices[0];
  if (!arr || !slice) return null;
  const { startIdx, endIdx } = slice;
  const filteredLap = arr.slice(startIdx, endIdx + 1);

  // Group contiguous segments of the same color
  const traces = [];
  let segStart = 0;
  let lastColor = getInputColor(filteredLap[0]);
  for (let i = 1; i < filteredLap.length; i++) {
    const color = getInputColor(filteredLap[i]);
    if (color !== lastColor || i === filteredLap.length - 1) {
      // End of current color segment
      const segEnd = (color !== lastColor) ? i : i + 1;
      if (segEnd - segStart > 1) {
        traces.push({
          x: filteredLap.slice(segStart, segEnd).map(p => p.position_x),
          y: filteredLap.slice(segStart, segEnd).map(p => -p.position_z),
          mode: 'lines',
          type: 'scatter',
          line: { color: lastColor, width: 5 },
          hoverinfo: 'none',
          showlegend: false,
        });
      }
      segStart = i;
      lastColor = color;
    }
  }

  // Animated marker for the highlighted lap
  let markerTrace = null;
  if (currentIndices && selectedLaps) {
    const lapIdx = selectedLaps.findIndex(id => id === lapId);
    if (lapIdx !== -1 && currentIndices[lapIdx] !== undefined) {
      let idx = currentIndices[lapIdx];
      if (idx < startIdx) idx = startIdx;
      if (idx > endIdx) idx = endIdx;
      const p = arr[idx];
      markerTrace = {
        x: [p.position_x],
        y: [-p.position_z],
        type: 'scatter',
        mode: 'markers',
        marker: {
          color: markerColor || '#00AEEF',
          size: 12,
          symbol: 'circle',
          line: { color: '#fff', width: 2 },
        },
        name: `Replay Marker`,
        showlegend: false,
        hoverinfo: 'skip',
      };
    }
  }

  // Track borders
  const borderTraces = [];
  if (trackLayout && trackLayout.inside) {
    borderTraces.push({
      x: trackLayout.inside.map(p => p.position_x ?? p[0]),
      y: trackLayout.inside.map(p => -(p.position_z ?? p[1])),
      mode: 'lines',
      line: { color: 'rgba(200,200,200,0.5)', width: 2 },
      type: 'scatter',
      name: 'Track Inside',
      showlegend: false,
    });
  }
  if (trackLayout && trackLayout.outside) {
    borderTraces.push({
      x: trackLayout.outside.map(p => p.position_x ?? p[0]),
      y: trackLayout.outside.map(p => -(p.position_z ?? p[1])),
      mode: 'lines',
      line: { color: 'rgba(200,200,200,0.5)', width: 2 },
      type: 'scatter',
      name: 'Track Outside',
      showlegend: false,
    });
  }

  return (
    <Plot
      data={markerTrace ? [...traces, markerTrace, ...borderTraces] : [...traces, ...borderTraces]}
      layout={{
        autosize: true,
        width: null,
        height: 500,
        margin: { l: 40, r: 40, t: 40, b: 40 },
        dragmode: 'pan',
        xaxis: {
          title: 'X Position',
          gridcolor: '#333',
          zerolinecolor: '#333',
          range: axisRanges?.x,
        },
        yaxis: {
          title: 'Y Position',
          gridcolor: '#333',
          zerolinecolor: '#333',
          scaleanchor: 'x',
          range: axisRanges?.y,
        },
        plot_bgcolor: '#1E1E1E',
        paper_bgcolor: '#1E1E1E',
        font: { color: '#fff' },
        showlegend: false,
      }}
      config={{
        responsive: true,
        displayModeBar: true, // Show Plotly modebar controls
        scrollZoom: true,
      }}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler={true}
      onRelayout={onRelayout}
    />
  );
}

RaceLineHighlight.propTypes = {
  lapId: PropTypes.string.isRequired,
  lapData: PropTypes.object.isRequired,
  trackLayout: PropTypes.object,
  distanceSliceIndices: PropTypes.array.isRequired,
  axisRanges: PropTypes.object,
  onRelayout: PropTypes.func,
  currentIndices: PropTypes.array,
  selectedLaps: PropTypes.array,
  markerColor: PropTypes.string,
};
